import type { RoofGenerationStageInput, RoofGenerationStageResult } from "building-types/roofs/roofGenerationTypes";
import { RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { buildEdgePolygon, buildBSPNode } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { polyLine2EdgeCount, polyLine2EdgeStart, polyLine2EdgeEnd } from "geom/polyLine2";
import { clipRoofWallFaces } from "../roofWallFaces";

/**
 * Clips the gable end faces using the wall perimeters
 */
export function generateRoofGableClipping({
  subRoof,
  subRoofOptions,
}: RoofGenerationStageInput): RoofGenerationStageResult {
  const { roofVolume, roofSettings, wallFaces } = subRoof;
  const { issues } = subRoofOptions;
  const { perimeters } = roofVolume;
  const { startWallFaces, endWallFaces } = wallFaces;

  const { offsetWallPerimeter, wallPerimeter } = perimeters;

  const { boundaryType = RoofBoundaryType.BOUNDING_BOX } = roofSettings;

  // end face clipping
  let clipEndFacesBSP: NodeBSP | null = null;
  let clipEndTrimsBSP: NodeBSP | null = null;
  if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER) {
    const clipEndPolygons: PolygonBSP[] = [];
    for (let i = 0, l = polyLine2EdgeCount(wallPerimeter); i < l; i++) {
      const [ax, ay] = polyLine2EdgeStart(wallPerimeter, i);
      const [bx, by] = polyLine2EdgeEnd(wallPerimeter, i);

      const polygon = buildEdgePolygon(bx, by, 0, ax, ay, 0, 0, 0, 1);
      if (!polygon) {
        continue;
      }

      clipEndPolygons.push(polygon);
    }

    clipEndFacesBSP = buildBSPNode({
      polygons: clipEndPolygons,
      epsilon: MULTIGABLE_BSP_EPSILON,
      maxDepthErrorHandle: getMaxDepthErrorHandle(issues, { type: "ROOF", id: subRoof.id }),
    });

    const clipEndTrimsPolygons: PolygonBSP[] = [];
    for (let i = 0, l = polyLine2EdgeCount(offsetWallPerimeter); i < l; i++) {
      const [ax, ay] = polyLine2EdgeStart(offsetWallPerimeter, i);
      const [bx, by] = polyLine2EdgeEnd(offsetWallPerimeter, i);

      const polygon = buildEdgePolygon(ax, ay, 0, bx, by, 0, 0, 0, 1);

      if (!polygon) {
        continue;
      }

      clipEndTrimsPolygons.push(polygon);
    }

    clipEndTrimsBSP = buildBSPNode({ polygons: clipEndTrimsPolygons, epsilon: MULTIGABLE_BSP_EPSILON });

    const clipEndFacesOptions: ClipFaceContext = {
      clippingNodes: [clippingNodeNew(clipEndFacesBSP)],
      coplanarsToFront: true,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    clipRoofWallFaces(startWallFaces, clipEndFacesOptions);
    clipRoofWallFaces(endWallFaces, clipEndFacesOptions);
  }

  return {
    subRoof: {},
    temporaries: {
      clipEndFacesBSP: clipEndFacesBSP ?? undefined,
      clipEndTrimsBSP: clipEndTrimsBSP ?? undefined,
    },
  };
}
